import { useState } from 'react'

import dayjs from 'dayjs'

import { useAnalyticsRecommendationPerformanceGetApi } from '@/common/api/modules/analytics'
import { useAuthorizedMe } from '@/common/api/modules/auth'
import { useSiteDashboardGetApi, useSiteGetApi } from '@/common/api/modules/site'
import { GetRecommendationPerformanceResponse } from '@/common/api/params/analytics'
import { SiteDashboardResponse } from '@/common/api/params/site'
import { currencyText } from '@/common/app/currency'
import {
  customersPath,
  orderHistoryPath,
  w2FeaturesPath,
  w2ProductCategoriesPath,
  w2ProductInventoryPath,
  w2ProductsPath,
  widgetListPath
} from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { dateStr, dateTimeStr } from '@/common/form/date'
import {
  ConversionAttribution,
  DEFAULT_CONVERSION_ATTRIBUTION
} from '@/common/model/recommend/conversion'
import { Site } from '@/common/model/site'
import { ChildProps } from '@/common/util/props'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import ProgressBar from '@/components/view/atoms/bar/RateBar'
import IconButton from '@/components/view/atoms/button/IconButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import LineChart from '@/components/view/atoms/chart/LineChart'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import SimplePager from '@/components/view/atoms/pager/SimplePager'
import TextPager from '@/components/view/atoms/pager/TextPager'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import DisclosureWrapper from '@/components/view/layouts/disclosure/DisclosureWrapper'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import MetricItem from '@/components/view/molecules/metric/MetricItem'
import SummaryCell from '@/components/view/molecules/summary/SummaryCell'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import MainInnerLoader from '@/components/container/MainInnerLoader'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import SamplingRateButton from '@/components/container/modules/admin/site/table/SamplingRateButton'
import ConversionAttributionToggle from '@/components/container/modules/recommend/ConversionAttributionToggle'

const SummaryCellList = ({ children }: ChildProps) => {
  return <div className='grid grid-cols-3 grid-rows-3 gap-2 p-4'>{children}</div>
}

interface SiteSummaryProps {
  site: Site
  dashboard: SiteDashboardResponse
}

const SiteSummary = ({ site, dashboard }: SiteSummaryProps) => {
  return (
    <>
      <SummaryCellList>
        <SummaryCell vertical link={w2ProductsPath(site.id)} color={'orange'}>
          <IconText
            text='商品'
            iconType={IconType.PRODUCT}
            color={IconColor.ORANGE}
            textColor={TextColor.ORANGE}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.products.toLocaleString()}
            </Text>
            <Text size={14}>件</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={w2ProductCategoriesPath(site.id)} color={'orange'}>
          <IconText
            text='カテゴリ'
            iconType={IconType.PRODUCT}
            color={IconColor.ORANGE}
            textColor={TextColor.ORANGE}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.categories.toLocaleString()}
            </Text>
            <Text size={14}>件</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={w2ProductInventoryPath(site.id)} color={'orange'}>
          <IconText
            text='商品在庫'
            iconType={IconType.PRODUCT}
            color={IconColor.ORANGE}
            textColor={TextColor.ORANGE}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.inventory.toLocaleString()}
            </Text>
            <Text size={14}>件</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={customersPath(site.id)} color={'purple'}>
          <IconText
            text='顧客'
            iconType={IconType.USER_SQUARE}
            color={IconColor.PURPLE}
            textColor={TextColor.PURPLE}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.customers_total.toLocaleString()}
            </Text>
            <Text size={14}>人</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={customersPath(site.id)} color={'purple'}>
          <IconText
            text='会員'
            iconType={IconType.USER_SQUARE}
            color={IconColor.PURPLE}
            textColor={TextColor.PURPLE}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.customers_member.toLocaleString()}
            </Text>
            <Text size={14}>人</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={customersPath(site.id)} color={'purple'}>
          <IconText
            text='ゲスト'
            iconType={IconType.USER_SQUARE}
            color={IconColor.PURPLE}
            textColor={TextColor.PURPLE}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.customers_unknown.toLocaleString()}
            </Text>
            <Text size={14}>人</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={orderHistoryPath(site.id)} color={'red'}>
          <IconText
            text='受注'
            iconType={IconType.INBOX}
            color={IconColor.BENI}
            textColor={TextColor.BENI}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.orders.toLocaleString()}
            </Text>
            <Text size={14}>件</Text>
          </ItemAlign>
        </SummaryCell>
        <SummaryCell vertical link={w2FeaturesPath(site.id)} color={'green'}>
          <IconText
            text='特集ページ'
            iconType={IconType.CONTENTS}
            color={IconColor.GREEN}
            textColor={TextColor.GREEN}
            direction='horizon'
          />
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.features.toLocaleString()}
            </Text>
            <Text size={14}>件</Text>
          </ItemAlign>
        </SummaryCell>
      </SummaryCellList>
    </>
  )
}

interface DeliverySettingProps {
  site: Site
  dashboard: SiteDashboardResponse
  reload: () => void
}

const DeliverySetting = ({ site, dashboard, reload }: DeliverySettingProps) => {
  const { isAdmin } = useAuthorizedMe()
  return (
    <>
      <div className='w-full p-4'>
        <div className='flex gap-2 pb-2'>
          <div className='w-1/3'>
            <SummaryCell vertical link={widgetListPath(site.id)} noFull>
              <Text weight='bold'>レコメンド</Text>
              <ItemAlign>
                <Text weight='bold' size={24}>
                  {dashboard.widgets}
                </Text>
                <Text size={14}>件</Text>
              </ItemAlign>
            </SummaryCell>
          </div>
          <SummaryCell vertical>
            <div className='flex justify-between pb-1'>
              <Text weight='bold'>レコメンド配信率</Text>
              {isAdmin && (
                <SamplingRateButton
                  siteId={site.id}
                  samplingRate={site.rec_sampling_rate}
                  onComplete={() => {
                    reload()
                  }}
                />
              )}
            </div>
            <ProgressBar percent={site.rec_sampling_rate} />
          </SummaryCell>
        </div>
        <div className='border-primary bg-fill-surface-tertiary flex flex-col gap-1.5 rounded-lg border p-4'>
          <TextButton>
            <Text weight='bold'>モデルチューニング</Text>
            <Icon type={IconType.ARROW_RIGHT} color={IconColor.LINK} />
          </TextButton>
          <FormRow align='center' gap={12}>
            <FormLabel fixWidth={80}>最終実行</FormLabel>
            <ItemAlign gapX={4}>
              <Text>
                {dashboard.retrain_last_at ? dateTimeStr(dashboard.retrain_last_at) : '未実行'}
              </Text>
              {dashboard.retrain_status && (
                <Badge badgeType={BadgeType.SUCCESS} size={10}>
                  {dashboard.retrain_status == 'complete'
                    ? '成功'
                    : dashboard.retrain_status == 'error'
                      ? 'エラー'
                      : '-'}
                </Badge>
              )}
            </ItemAlign>
          </FormRow>
          <FormRow align='center' gap={12}>
            <FormLabel fixWidth={80}>データ蓄積量</FormLabel>
            <MetricItem label='イベント' value={dashboard.events_count.toLocaleString()} />
            <MetricItem label='受注' value={dashboard.orders_count.toLocaleString()} />
            <MetricItem label='ID紐付' value={dashboard.identities_count.toLocaleString()} />
          </FormRow>
          <FormRow align='center' gap={12}>
            <ItemAlign gapX={4} nowrap align='center'>
              <Text size={12} nowrap weight='bold'>
                モデル精度
              </Text>
              {dashboard.retrain_detail && <ModelAccuracyHelp detail={dashboard.retrain_detail} />}
            </ItemAlign>
            {dashboard.retrain_detail ? (
              <>
                <ItemAlign gapX={4}>
                  <Badge badgeType={BadgeType.SQUARE_TERTIARY_SECONDARY}>RMSE</Badge>
                  <Text
                    weight='bold'
                    size={14}
                    color={
                      dashboard.retrain_detail?.collaborative?.rmse &&
                      dashboard.retrain_detail?.collaborative?.rmse > 0
                        ? TextColor.SUCCESS
                        : TextColor.PRIMARY
                    }
                  >
                    {dashboard.retrain_detail?.collaborative?.rmse != undefined
                      ? dashboard.retrain_detail.collaborative.rmse.toFixed(4)
                      : '-'}
                  </Text>
                </ItemAlign>
                <MetricItem
                  label='学習データ'
                  value={
                    dashboard.retrain_detail?.collaborative?.n_ratings != undefined
                      ? `${dashboard.retrain_detail.collaborative.n_ratings.toLocaleString()} 件`
                      : '-'
                  }
                />
                <MetricItem
                  label='商品'
                  value={
                    dashboard.retrain_detail?.content_based?.n_products != undefined
                      ? `${dashboard.retrain_detail.content_based.n_products.toLocaleString()} 件`
                      : '-'
                  }
                />
                <ItemAlign gapX={4}>
                  <Badge badgeType={BadgeType.SQUARE_TERTIARY}>精度</Badge>
                  <ItemAlign gapX={2}>
                    <Text
                      weight='bold'
                      size={14}
                      color={
                        dashboard.retrain_detail?.neural?.accuracy &&
                        dashboard.retrain_detail?.neural?.accuracy > 0.7
                          ? TextColor.SUCCESS
                          : TextColor.PRIMARY
                      }
                    >
                      {dashboard.retrain_detail?.neural?.accuracy != undefined
                        ? (dashboard.retrain_detail.neural.accuracy * 100).toFixed(1)
                        : '-'}
                    </Text>
                    <Text>%</Text>
                  </ItemAlign>
                </ItemAlign>
                <MetricItem
                  label='Loss'
                  value={
                    dashboard.retrain_detail?.neural?.loss != undefined
                      ? dashboard.retrain_detail.neural.loss.toFixed(4)
                      : '-'
                  }
                />
              </>
            ) : (
              <Text>未学習（モデルチューニング実行後に表示されます）</Text>
            )}
          </FormRow>
        </div>
      </div>
    </>
  )
}

type EvalLevel = 'good' | 'normal' | 'warning' | null

const evalColor = (level: EvalLevel): string => {
  switch (level) {
    case 'good':
      return 'text-success'
    case 'normal':
      return 'text-warning'
    case 'warning':
      return 'text-critical'
    default:
      return 'text-secondary'
  }
}

const evalLabel = (level: EvalLevel): string => {
  switch (level) {
    case 'good':
      return '良好'
    case 'normal':
      return '普通'
    case 'warning':
      return '要改善'
    default:
      return '-'
  }
}

const ModelAccuracyHelp = ({
  detail
}: {
  detail: NonNullable<SiteDashboardResponse['retrain_detail']>
}) => {
  const rmse = detail?.collaborative?.rmse
  const nRatings = detail?.collaborative?.n_ratings
  const nProducts = detail?.content_based?.n_products
  const accuracy = detail?.neural?.accuracy
  const loss = detail?.neural?.loss

  const evalRmse = (v?: number): EvalLevel =>
    v == undefined ? null : v <= 0.1 ? 'good' : v <= 0.5 ? 'normal' : 'warning'
  const evalRatings = (v?: number): EvalLevel =>
    v == undefined ? null : v >= 10000 ? 'good' : v >= 1000 ? 'normal' : 'warning'
  const evalAccuracy = (v?: number): EvalLevel =>
    v == undefined ? null : v >= 0.9 ? 'good' : v >= 0.7 ? 'normal' : 'warning'
  const evalLoss = (v?: number): EvalLevel =>
    v == undefined ? null : v <= 0.3 ? 'good' : v <= 0.5 ? 'normal' : 'warning'

  const items: {
    name: string
    valueText: string
    desc: string
    criteria: string
    level: EvalLevel
  }[] = [
    {
      name: 'RMSE',
      valueText: rmse != undefined ? rmse.toFixed(4) : '-',
      desc: '予測スコアと実行動の誤差。値が小さいほど予測が正確。',
      criteria: '良好: 0.1以下 / 普通: 0.1〜0.5 / 要改善: 0.5以上',
      level: evalRmse(rmse)
    },
    {
      name: '学習データ',
      valueText: nRatings != undefined ? `${nRatings.toLocaleString()} 件` : '-',
      desc: 'ユーザー × 商品の行動データ件数。',
      criteria: '優秀: 10,000件以上 / 安定: 1,000件以上 / 蓄積中: 1,000件未満',
      level: evalRatings(nRatings)
    },
    {
      name: '商品',
      valueText: nProducts != undefined ? `${nProducts.toLocaleString()} 件` : '-',
      desc: 'レコメンド対象の商品マスタ件数。',
      criteria: '',
      level: null
    },
    {
      name: '精度',
      valueText: accuracy != undefined ? `${(accuracy * 100).toFixed(1)} %` : '-',
      desc: '購入する／しないの予測正答率。',
      criteria: '優秀: 90%以上 / 普通: 70〜89% / 要改善: 70%未満',
      level: evalAccuracy(accuracy)
    },
    {
      name: 'Loss',
      valueText: loss != undefined ? loss.toFixed(4) : '-',
      desc: '学習中の誤差。値が小さいほど学習が進んでいる。',
      criteria: '良好: 0.3以下 / 普通: 0.3〜0.5 / 要改善: 0.5以上',
      level: evalLoss(loss)
    }
  ]

  const [showing, setShowing] = useState(false)

  return (
    <DisclosureWrapper
      disclosureNode={
        <>
          <div className='border-secondary mb-3 border-b pb-2 text-sm font-bold'>
            モデル精度の指標について
          </div>
          <div className='flex flex-col gap-3'>
            {items.map((it) => (
              <div key={it.name}>
                <div className='mb-1 flex flex-wrap items-center gap-x-2 gap-y-1'>
                  <span className='text-xs font-bold'>{it.name}</span>
                  <span className='text-xs'>{it.valueText}</span>
                  {it.level && (
                    <span className={`text-[10px] font-bold ${evalColor(it.level)}`}>
                      {evalLabel(it.level)}
                    </span>
                  )}
                </div>
                <div className='text-secondary text-[11px] leading-relaxed'>
                  {it.desc}
                  {it.criteria && (
                    <>
                      <br />
                      目安: {it.criteria}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      }
      off={!showing}
      onClickOutSide={() => setShowing(false)}
    >
      <IconButton type={IconType.HELP} onClick={() => setShowing(!showing)} line />
    </DisclosureWrapper>
  )
}

const TransitionSvg = () => {
  return (
    <svg
      width='10'
      height='81'
      viewBox='0 0 10 81'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className='shrink-0'
    >
      <path d='M1.5 16L1.5 65' stroke='#3F4EB0' />
      <g filter='url(#filter0_d_292_25013)'>
        <path d='M1.41464e-06 49L0 33L8 41L1.41464e-06 49Z' fill='white' />
      </g>
      <defs>
        <filter
          id='filter0_d_292_25013'
          x='0'
          y='33'
          width='9.5'
          height='16'
          filterUnits='userSpaceOnUse'
          color-interpolation-filters='sRGB'
        >
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
            result='hardAlpha'
          />
          <feOffset dx='1.5' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix
            type='matrix'
            values='0 0 0 0 0.247059 0 0 0 0 0.305882 0 0 0 0 0.690196 0 0 0 1 0'
          />
          <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_292_25013' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='effect1_dropShadow_292_25013'
            result='shape'
          />
        </filter>
      </defs>
    </svg>
  )
}

interface RecommendResultProps {
  dashboard: SiteDashboardResponse
  recommendationPerformance: GetRecommendationPerformanceResponse
  // 表示するコンバージョン種別。省略時はアシスト（既定）
  attribution?: ConversionAttribution
}

export const RecommendResult = ({
  dashboard,
  recommendationPerformance,
  attribution = DEFAULT_CONVERSION_ATTRIBUTION
}: RecommendResultProps) => {
  // 表示中のコンバージョン種別の受注。合算はせず、どちらか一方だけを見る
  const orders = dashboard.recommend_orders?.[attribution]
  const monthOrders = orders?.month ?? 0
  const monthAmount = orders?.month_amount ?? 0

  // レコメンドシェア率は件数と金額の2本立て。
  // 件数だけでは「単価の高い商品がレコメンド経由で売れた」ことが見えず、
  // 金額だけでは少数の高額注文に引っ張られていることが分からない。
  // 分子・分母とも注文単位で数えているため 100% を超えない。受注が0件の月は算出できない。
  const recommendShareRate =
    dashboard.month_orders_total > 0 ? (monthOrders / dashboard.month_orders_total) * 100 : null
  const recommendAmountShareRate =
    dashboard.month_amount_total > 0 ? (monthAmount / dashboard.month_amount_total) * 100 : null

  // 配信ベースCVR（受注数 / 表示回数）。分子だけが種別で切り替わる
  const cvrConversion =
    recommendationPerformance.total_impressions > 0
      ? monthOrders / recommendationPerformance.total_impressions
      : 0

  return (
    <SummaryItemList vertical>
      <div className='border-primary flex border-b'>
        <SummaryItem>
          <Text weight='bold'>サイトPV数</Text>
          <ItemAlign>
            <Text weight='bold' size={24}>
              {dashboard.month_page_views.toLocaleString()}
            </Text>
            <Text size={14}>件</Text>
          </ItemAlign>
        </SummaryItem>
        <SummaryItem>
          <IconText
            text='売上高'
            iconType={IconType.INBOX}
            color={IconColor.CRITICAL}
            textColor={TextColor.BENI}
            direction='horizon'
          />
          <ItemAlign>
            <Text size={14}>¥</Text>
            <Text weight='bold' size={24}>
              {Math.round(monthAmount).toLocaleString()}
            </Text>
          </ItemAlign>
        </SummaryItem>
        <SummaryItem>
          <ItemAlign align='center'>
            <IconText
              text='シェア率'
              iconType={IconType.INBOX}
              color={IconColor.CRITICAL}
              textColor={TextColor.BENI}
              direction='horizon'
            />
            <Text color={TextColor.SECONDARY} size={10}>
              件数
            </Text>
          </ItemAlign>
          <ItemAlign>
            <Text weight='bold' size={24}>
              {recommendShareRate === null ? '-' : recommendShareRate.toFixed(2)}
            </Text>
            <Text size={14}>%</Text>
          </ItemAlign>
        </SummaryItem>
        <SummaryItem>
          <ItemAlign align='center'>
            <IconText
              text='シェア率'
              iconType={IconType.INBOX}
              color={IconColor.CRITICAL}
              textColor={TextColor.BENI}
              direction='horizon'
            />
            <Text color={TextColor.SECONDARY} size={10}>
              金額
            </Text>
          </ItemAlign>
          <ItemAlign>
            <Text weight='bold' size={24}>
              {recommendAmountShareRate === null ? '-' : recommendAmountShareRate.toFixed(2)}
            </Text>
            <Text size={14}>%</Text>
          </ItemAlign>
        </SummaryItem>
      </div>
      <div>
        <ItemAlign align='center' gapX={16} nowrap full>
          <div className='flex w-full flex-col items-center gap-2'>
            <IconText
              text='表示'
              iconType={IconType.WEB_PAGE}
              color={IconColor.PRIMARY}
              textColor={TextColor.PRIMARY}
              direction='horizon'
              nowrap
            />
            <ItemAlign gapX={4} nowrap>
              <Text weight='bold' size={24} nowrap>
                {recommendationPerformance.total_impressions.toLocaleString()}
              </Text>
              <Text>件</Text>
            </ItemAlign>
          </div>
          <TransitionSvg />
          <div className='flex w-full items-center gap-2'>
            <div className='flex w-full flex-col items-center gap-2'>
              <IconText
                text='クリック'
                iconType={IconType.MOUSE_POINTER}
                color={IconColor.PRIMARY}
                textColor={TextColor.PRIMARY}
                direction='horizon'
                nowrap
              />
              <ItemAlign gapX={4} nowrap>
                <Text weight='bold' size={24} nowrap>
                  {recommendationPerformance.total_clicks.toLocaleString()}
                </Text>
                <Text>件</Text>
              </ItemAlign>
            </div>
            <div className='flex w-full flex-col items-center gap-2'>
              <IconText
                text='クリック率'
                iconType={IconType.MOUSE_POINTER}
                color={IconColor.PRIMARY}
                textColor={TextColor.PRIMARY}
                direction='horizon'
                nowrap
              />
              <ItemAlign gapX={4} nowrap>
                <Text weight='bold' size={24} nowrap>
                  {(recommendationPerformance.ctr * 100).toFixed(2)}
                </Text>
                <Text>%</Text>
              </ItemAlign>
            </div>
          </div>
          <TransitionSvg />
          <div className='flex w-full items-center gap-2'>
            <div className='flex w-full flex-col items-center gap-2'>
              <IconText
                text='CV（購入）'
                iconType={IconType.INBOX}
                color={IconColor.CRITICAL}
                textColor={TextColor.BENI}
                direction='horizon'
                nowrap
              />
              <ItemAlign nowrap>
                <Text weight='bold' size={24} nowrap>
                  {monthOrders.toLocaleString()}
                </Text>
                <Text>件 </Text>
              </ItemAlign>
            </div>
            <div className='flex w-full flex-col items-center gap-2'>
              <IconText
                text='CVR'
                iconType={IconType.INBOX}
                color={IconColor.CRITICAL}
                textColor={TextColor.BENI}
                direction='horizon'
                nowrap
              />
              <ItemAlign nowrap>
                <Text weight='bold' size={24} nowrap>
                  {(cvrConversion * 100).toFixed(2)}
                </Text>
                <Text size={14}>%</Text>
              </ItemAlign>
            </div>
          </div>
        </ItemAlign>
      </div>
    </SummaryItemList>
  )
}

interface DashboardProps {
  dashboard: SiteDashboardResponse
  // 受注推移に使うコンバージョン種別。省略時はアシスト（既定）
  attribution?: ConversionAttribution
}

const ActionChart = ({
  dashboard,
  attribution = DEFAULT_CONVERSION_ATTRIBUTION
}: DashboardProps) => {
  // 受注は種別ごとに数えた日別を使う。従来キー（daily_orders / prev_orders）は
  // 直接CVを指すため、種別が取れないときだけそちらへ落とす
  const ordersByAttribution = dashboard.recommend_orders?.[attribution]
  const dailyOrders = ordersByAttribution?.daily ?? dashboard.daily_orders ?? []
  const prevOrders = ordersByAttribution?.prev_daily ?? dashboard.prev_orders ?? []

  // 当月データ（横軸は月の全日数を固定表示）
  const behaviorLabels = (dashboard.daily_behavior || []).map((d) => dayjs(d.date).format('D'))
  const behaviorValues = (dashboard.daily_behavior || []).map((d) => d.count)
  const orderLabels = dailyOrders.map((d) => dayjs(d.date).format('D'))
  const orderValues = dailyOrders.map((d) => d.count)

  // 前月データ（日数が異なる場合があるため、表示月の日数分だけ取得）
  const prevBehaviorValues = (dashboard.prev_behavior || []).map((d) => d.count)
  const prevOrderValues = prevOrders.map((d) => d.count)

  return (
    <div className='flex'>
      <ChartWrapper half='left'>
        <ChartTitle>行動推移</ChartTitle>
        <LineChart
          labels={behaviorLabels}
          values={behaviorValues}
          preValues={prevBehaviorValues}
          currentValueLabel='今月'
          previousValueLabel='前月'
        />
      </ChartWrapper>
      <ChartWrapper half='right'>
        <ChartTitle>受注推移</ChartTitle>
        <LineChart
          labels={orderLabels}
          values={orderValues}
          preValues={prevOrderValues}
          currentValueLabel='今月'
          previousValueLabel='前月'
        />
      </ChartWrapper>
    </div>
  )
}

interface TransitionProps {
  dashboard: SiteDashboardResponse
  recommendationPerformance: GetRecommendationPerformanceResponse
  // 表示するコンバージョン種別。省略時はアシスト（既定）
  attribution?: ConversionAttribution
}

export const ActionTransition = ({
  dashboard,
  recommendationPerformance,
  attribution = DEFAULT_CONVERSION_ATTRIBUTION
}: TransitionProps) => {
  const isDirect = attribution === ConversionAttribution.DIRECT
  return (
    <Area noShadow>
      <ActionChart dashboard={dashboard} attribution={attribution} />
      <Table>
        <thead>
          <TableRow head>
            <TableHeadItem>
              <Text size={12}>日付</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text size={12}>総表示数</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text size={12}>クリック数</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text size={12}>CV数（導入）</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text size={12}>売上高</Text>
            </TableHeadItem>
          </TableRow>
        </thead>
        <tbody>
          {recommendationPerformance.daily.map((day) => (
            <TableRow key={day.date}>
              <TableRowItem>
                <Text>{dateStr(day.date)}</Text>
              </TableRowItem>
              <TableRowItem>
                <Text>{day.impressions.toLocaleString()}</Text>
              </TableRowItem>
              <TableRowItem>
                <Text>{day.clicks.toLocaleString()}</Text>
              </TableRowItem>
              <TableRowItem>
                <Text>
                  {(isDirect ? day.direct_conversions : day.assist_conversions).toLocaleString()}
                </Text>
              </TableRowItem>
              <TableRowItem>
                <Text>{currencyText(isDirect ? day.direct_revenue : day.assist_revenue)}</Text>
              </TableRowItem>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </Area>
  )
}

const SiteDashboardPage = () => {
  const siteId = useSiteId()

  const [date, setDate] = useState<Date | null>(null)
  // 表示するコンバージョン種別。直接とアシストは合算しないため、切り替えで見る
  const [attribution, setAttribution] = useState<ConversionAttribution>(
    DEFAULT_CONVERSION_ATTRIBUTION
  )

  const formattedMonth = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    : null

  const {
    data: site,
    isLoading: isSiteLoading,
    error: siteError,
    mutate
  } = useSiteGetApi({
    site_id: siteId
  })
  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    error: dashboardError
  } = useSiteDashboardGetApi({
    site_id: siteId,
    ...(formattedMonth && {
      month: formattedMonth
    })
  })
  const {
    data: recommendationPerformance,
    isLoading: isPerformanceLoading,
    error: performanceError
  } = useAnalyticsRecommendationPerformanceGetApi({
    site_id: siteId,
    ...(formattedMonth && {
      month: formattedMonth
    })
  })

  return (
    <>
      <Main type={MainLayoutType.FULL}>
        <PageTitle
          title={'ダッシュボード' + (site ? `：${site.name}様` : '')}
          behindNode={
            site ? (
              site.is_active ? (
                <Badge badgeType={BadgeType.SUCCESS}>稼働中</Badge>
              ) : (
                <Badge badgeType={BadgeType.SUCCESS}>非稼働</Badge>
              )
            ) : undefined
          }
          rightNode={
            <>
              {/* For design */}
              {/* <OperationButton /> */}
            </>
          }
          badgeNode={<AppTitleBadge type='recommend' />}
        />
        <MainInnerLoader
          error={siteError || dashboardError || performanceError}
          data={site && dashboard && recommendationPerformance}
        >
          {site && dashboard && recommendationPerformance && (
            <Content>
              <Area noBorder>
                <SummaryItemList>
                  <SummaryItem>
                    <ItemAlign>
                      <Text weight='bold'>今日の行動数</Text>
                      <Text color={TextColor.SECONDARY} size={10}>
                        {dayjs().format('YYYY年MM月DD日')}
                      </Text>
                    </ItemAlign>
                    <ItemAlign>
                      <Text weight='bold' size={24}>
                        {dashboard.today_behavior}
                      </Text>
                      <Text size={14}>件</Text>
                    </ItemAlign>
                  </SummaryItem>
                  <SummaryItem>
                    <ItemAlign>
                      <Text weight='bold' size={12}>
                        今日の受注
                      </Text>
                      <Text color={TextColor.SECONDARY} size={10}>
                        {dayjs().format('YYYY年MM月DD日')}
                      </Text>
                    </ItemAlign>
                    <ItemAlign>
                      <Text weight='bold' size={24}>
                        {dashboard.today_orders}
                      </Text>
                      <Text size={14}>件</Text>
                    </ItemAlign>
                  </SummaryItem>
                </SummaryItemList>
              </Area>
              <div className='flex gap-6'>
                <div className='w-full'>
                  <SummaryHeading>サイトサマリ</SummaryHeading>
                  <Area noShadow>
                    <SiteSummary site={site} dashboard={dashboard} />
                  </Area>
                </div>
                <div className='w-full'>
                  <SummaryHeading>配信設定</SummaryHeading>
                  <Area noShadow>
                    <DeliverySetting site={site} dashboard={dashboard} reload={() => mutate()} />
                  </Area>
                </div>
              </div>
              <div>
                <SummaryHeading>
                  <ItemAlign justify='between'>
                    <ItemAlign align='center'>
                      レコメンド成果
                      <TextPager
                        value={dayjs(date ?? new Date()).format('YYYY年M月')}
                        onClickPrev={() => {
                          setDate((date) =>
                            dayjs(date ?? new Date())
                              .subtract(1, 'month')
                              .toDate()
                          )
                        }}
                        onClickNext={
                          date && dayjs().startOf('month').isAfter(dayjs(date).startOf('month'))
                            ? () => {
                                setDate((date) =>
                                  dayjs(date ?? new Date())
                                    .add(1, 'month')
                                    .toDate()
                                )
                              }
                            : undefined
                        }
                      />
                    </ItemAlign>
                    <ConversionAttributionToggle value={attribution} onSelect={setAttribution} />
                  </ItemAlign>
                </SummaryHeading>
                <Area noBorder>
                  <RecommendResult
                    dashboard={dashboard}
                    recommendationPerformance={recommendationPerformance}
                    attribution={attribution}
                  />
                </Area>
              </div>
              <ActionTransition
                dashboard={dashboard}
                recommendationPerformance={recommendationPerformance}
                attribution={attribution}
              />
            </Content>
          )}
        </MainInnerLoader>
      </Main>
    </>
  )
}

export default SiteDashboardPage
